/*
 * Account drawer: style the <shop-login-button> shadow DOM.
 *
 * Shopify renders <shop-login-button> with an open shadow root and uses
 * adoptedStyleSheets internally. External CSS cannot reach inside, so we
 * push a CSSStyleSheet into its shadowRoot.adoptedStyleSheets here.
 *
 * The internal markup (button > span > svg) is not a public API and can
 * change at any time — keep this override sheet small and easy to update.
 */
(function () {
  var SHEET_KEY = '__branchAccountDrawerSheet';
  var STYLE_EL_KEY = '__branchAccountDrawerStyleEl';
  var HOOKED_KEY = '__branchAccountDrawerHooked';

  var CSS = [
    'button {',
    '  background-color: var(--color-primary, #314438) !important;',
    '  border: 2px solid var(--color-primary, #314438) !important;',
    '  border-radius: var(--border-radius, 0) !important;',
    '  width: 100% !important;',
    '  min-height: 40px !important;',
    '  padding: 14.75px !important;',
    '}',
    'button:hover {',
    '  background-color: transparent !important;',
    '}',
    'button > span {',
    '  background-color: transparent !important;',
    '  color: var(--color-white, #fff) !important;',
    '  font-weight: var(--font-weight-medium, 500) !important;',
    '  min-height: 0 !important;',
    '  padding: 0 !important;',
    '  font-family: Quicksand, sans-serif !important;',
    '  font-size: 14px !important;',
    '  text-transform: uppercase !important;',
    '  letter-spacing: .05em !important;',
    '  font-weight: 700 !important;',
    '  line-height: 1 !important;',
    '}',
    'button:hover span {',
    '  color: var(--color-primary, #314438) !important;',
    '}',
    'svg {',
    '  color: currentColor !important;',
    '  width: 20px !important;',
    '  height: 20px !important;',
    '  width: 50px !important;',
    '  height: 20px !important;',
    '  margin-top: -2px !important;',
    '}',
  ].join('\n');

  function applyStyles(el) {
    var root = el && el.shadowRoot;
    if (!root) return false;

    // Try adoptedStyleSheets first; cache our sheet per-element so we can
    // re-add it if the SDK reassigns root.adoptedStyleSheets to its own array.
    try {
      var sheet = el[SHEET_KEY];
      if (!sheet) {
        sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
        el[SHEET_KEY] = sheet;
      }
      if (root.adoptedStyleSheets.indexOf(sheet) === -1) {
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
      }
      return true;
    } catch (e) {
      // Fallback: append a <style> element. Re-append if the SDK wiped it.
      var styleEl = el[STYLE_EL_KEY];
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.textContent = CSS;
        el[STYLE_EL_KEY] = styleEl;
      }
      if (styleEl.parentNode !== root) root.appendChild(styleEl);
      return true;
    }
  }

  function hookElement(el) {
    if (el[HOOKED_KEY]) return;
    el[HOOKED_KEY] = true;

    applyStyles(el);

    // Re-apply whenever the SDK mutates the shadow tree (it hydrates async
    // and can replace inner DOM and/or reassign adoptedStyleSheets).
    if (el.shadowRoot) {
      new MutationObserver(function () { applyStyles(el); })
        .observe(el.shadowRoot, { childList: true, subtree: true });
    }

    // SDK emits a 'loaded' event when the feature element finishes loading.
    el.addEventListener('loaded', function () { applyStyles(el); });

    // Safety net: poll for ~2s to catch any signal we missed.
    var ticks = 0;
    var iv = setInterval(function () {
      applyStyles(el);
      if (++ticks >= 20) clearInterval(iv);
    }, 100);
  }

  function scan(scope) {
    (scope || document).querySelectorAll('shop-login-button').forEach(hookElement);
  }

  scan();

  // Re-scan once the custom element class is registered by the Shop SDK —
  // this is the moment shadow content is most likely to have finalized.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('shop-login-button').then(function () {
      scan();
    }).catch(function () {});
  }

  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches('shop-login-button')) hookElement(n);
        if (n.querySelectorAll) scan(n);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/*
 * Account drawer: populate the "Recently Viewed" slider (logged-in customers).
 *
 * The product handles come from the customer metafield custom.recently_viewed,
 * rendered into [data-recently-viewed-data] by account-actions.liquid. Views are
 * recorded server-side (so the list follows the customer across devices) via the
 * Branch App Proxy — see recordView() below — not a cookie. Every card is still
 * rendered by Liquid via the Section Rendering API, so prices/images/locale come
 * from the real `product` object:
 *   GET /products/{handle}?section_id=recently-viewed-card
 *
 * To keep the slider instant we prefetch on intent and cache in sessionStorage:
 *   - Hovering / focusing / touching the account trigger warms the per-handle
 *     fetches before the panel finishes opening (see the intent listeners).
 *   - Each card's HTML is cached in sessionStorage (and an in-memory promise
 *     map), so repeat opens and page-to-page navigation reuse it with no
 *     network request.
 */
(function () {
  var MAX_ITEMS = 5;
  var SECTION_ID = 'recently-viewed-card';
  var CACHE_PREFIX = 'rv-card:';
  var cache = {}; // handle -> Promise<cardHTML>; dedupes in-flight + resolved fetches
  var prefetched = false;
  var loadedDrawers = new WeakSet();

  // Both drawers carry the slider markup (the side flyout inlines it; the
  // mobile drawer gets it via account-actions). Scope lookups to each drawer
  // — the PDP related-products section also uses [data-recently-viewed].
  var DRAWERS = [
    { el: document.getElementById('side-account'), openClass: 'open' },
    { el: document.querySelector('.account-drawer'), openClass: 'account-drawer--open' }
  ].filter(function (d) { return d.el; });

  function currentHandle() {
    var m = window.location.pathname.match(/\/products\/([a-z0-9\-]+)/);
    return m ? m[1] : null;
  }

  // Handles come from the customer metafield (custom.recently_viewed), rendered
  // into [data-recently-viewed-data] by account-actions.liquid. Logged-out
  // visitors have no element -> []. The drawer no longer reads the cookie.
  function readHandles() {
    var el = document.querySelector('[data-recently-viewed-data]');
    if (!el) return [];
    var entries;
    try { entries = JSON.parse(el.textContent || 'null'); } catch (e) { return []; }
    if (!Array.isArray(entries)) return [];
    var current = currentHandle();
    var seen = {};
    return entries
      .map(function (item) { return item && item.handle; })
      .filter(function (h) {
        if (!h || h === current || seen[h]) return false;
        seen[h] = true;
        return true;
      })
      .slice(0, MAX_ITEMS);
  }

  // Strip the Section Rendering API wrapper (#shopify-section-…) and return the
  // card anchor's own markup. Returns '' for empty/unavailable products.
  function extractCardHTML(html) {
    if (!html) return '';
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var card = tmp.querySelector('.side-account__recently-viewed-item');
    return card ? card.outerHTML : '';
  }

  function sessionGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function sessionSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* full / disabled */ }
  }

  // Resolve a handle's card HTML, preferring (1) the in-memory promise map,
  // then (2) sessionStorage, then (3) the network. Only non-empty cards are
  // cached, so an unavailable product can still recover on a later visit.
  function fetchCardHTML(handle) {
    if (cache[handle]) return cache[handle];

    var stored = sessionGet(CACHE_PREFIX + handle);
    if (stored) {
      cache[handle] = Promise.resolve(stored);
      return cache[handle];
    }

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    cache[handle] = fetch(root + 'products/' + handle + '?section_id=' + SECTION_ID)
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(extractCardHTML)
      .then(function (cardHTML) {
        if (cardHTML) sessionSet(CACHE_PREFIX + handle, cardHTML);
        return cardHTML;
      })
      .catch(function () { return ''; });
    return cache[handle];
  }

  // Warm the per-handle fetches without rendering. Cheap to call repeatedly;
  // only the first call does work, and only when one of the drawers actually
  // has the slider markup, so logged-out visitors fire no requests.
  function prefetch() {
    if (prefetched) return;
    var hasList = DRAWERS.some(function (d) {
      return d.el.querySelector('[data-recently-viewed-list]');
    });
    if (!hasList) return;
    prefetched = true;
    readHandles().forEach(fetchCardHTML);
  }

  // Init the Swiper slider once cards are in. Swiper loads on demand on the
  // product template (opening the drawer is a pointerdown that triggers it),
  // so route through whenSwiperReady when the bundle isn't ready yet.
  function initSlider(drawer) {
    var el = drawer.querySelector('[data-recently-viewed-slider]');
    if (!el || el.swiper) return;
    if (typeof window.Swiper === 'undefined') {
      if (typeof window.whenSwiperReady === 'function') window.whenSwiperReady(function () { initSlider(drawer); });
      return;
    }
    var section = el.closest('[data-recently-viewed]') || drawer;
    new window.Swiper(el, {
      slidesPerView: 2.2,
      spaceBetween: 12,
      autoHeight: true,
      navigation: {
        prevEl: section.querySelector('[data-recently-viewed-prev]'),
        nextEl: section.querySelector('[data-recently-viewed-next]')
      }
    });
  }

  function render(drawer) {
    var section = drawer.querySelector('[data-recently-viewed]');
    var list = drawer.querySelector('[data-recently-viewed-list]');
    if (!section || !list) return; // logged-out drawer has no list

    var handles = readHandles();
    if (!handles.length) return;

    // fetchCardHTML reuses anything intent-prefetch already warmed, so this
    // usually resolves from cache rather than the network.
    Promise.all(handles.map(fetchCardHTML)).then(function (cards) {
      var frag = document.createDocumentFragment();
      var tmp = document.createElement('div');
      var count = 0;
      cards.forEach(function (cardHTML) {
        if (!cardHTML) return;
        tmp.innerHTML = cardHTML;
        var card = tmp.firstElementChild;
        if (!card) return;
        var slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(card);
        frag.appendChild(slide);
        count++;
      });
      if (count > 0) {
        list.innerHTML = '';
        list.appendChild(frag);
        section.hidden = false;
        initSlider(drawer);
      }
    });
  }

  function loadOnce(drawer) {
    if (loadedDrawers.has(drawer)) return;
    loadedDrawers.add(drawer);
    render(drawer);
  }

  // Prefetch the moment the user signals intent to open the drawer, so the
  // cards are usually fetched (or already cached) before the slide-in finishes.
  // mouseenter/focus/touchstart don't all bubble, so we delegate the bubbling
  // equivalents on document — this also covers the mobile + desktop triggers.
  function onIntent(event) {
    var target = event.target;
    if (target && target.closest && target.closest('[data-side-account-trigger]')) {
      prefetch();
    }
  }
  document.addEventListener('mouseover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  // On a PDP, record the view to the logged-in customer's metafield via the
  // Branch App Proxy. Logged-in only (gated by the [data-recently-viewed-data]
  // element account-actions.liquid renders), once per product per session, and
  // fired on idle as a non-blocking, fire-and-forget request so it never touches
  // page render. The pre-existing cookie write (jquery.products.js) is untouched
  // and still powers the logged-out PDP related-products carousel.
  function recordView() {
    if (!document.querySelector('[data-recently-viewed-data]')) return; // logged out
    var data = document.getElementById('recently-viewed-data');
    if (!data) return; // not a product page
    var handle = data.getAttribute('data-product-handle');
    var id = data.getAttribute('data-product-id');
    if (!handle) return;
    try {
      var sent = JSON.parse(window.sessionStorage.getItem('rv-sent') || '[]');
      if (sent.indexOf(handle) !== -1) return;
      sent.push(handle);
      window.sessionStorage.setItem('rv-sent', JSON.stringify(sent));
    } catch (e) { /* sessionStorage full/disabled — still safe to send */ }
    var send = function () {
      fetch('/apps/branch/recently-viewed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: id, handle: handle }),
        keepalive: true
      }).catch(function () {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(send, { timeout: 2000 });
    } else {
      setTimeout(send, 0);
    }
  }
  recordView();

  // Render the first time each drawer opens. Tracked per-drawer so the mobile
  // drawer and the side flyout each render independently of the other.
  DRAWERS.forEach(function (d) {
    if (d.el.classList.contains(d.openClass)) {
      loadOnce(d.el);
    } else {
      new MutationObserver(function () {
        if (d.el.classList.contains(d.openClass)) loadOnce(d.el);
      }).observe(d.el, { attributes: true, attributeFilter: ['class'] });
    }
  });

  // A live "Sign in with Shop" login (scripts_account_live_login.js) swaps the
  // drawer content in place. The swapped-in markup carries fresh slider nodes,
  // so clear the per-drawer guard and re-render any drawer that's open now;
  // closed drawers re-render via the open observer above on next open.
  document.addEventListener('account-content:updated', function () {
    prefetched = false;
    DRAWERS.forEach(function (d) {
      loadedDrawers.delete(d.el);
      if (d.el.classList.contains(d.openClass)) loadOnce(d.el);
    });
  });
})();

/*
 * Friendbuy: render the current customer's coupon count into the wallet
 * section of the account drawer. push(['getCoupons', fn]) is authenticated by
 * the authMessage/authSignature set up in friendbuy.liquid, so the SDK
 * returns data scoped to the signed-in shopper — no bearer token in the
 * browser. (push(['getRewards', fn]) is the broader reward set if we ever
 * need it.)
 */
// (function () {
//   var tries = 0;
//   var iv = setInterval(function () {
//     var api = window.friendbuyAPI;
//     if (api && api.loaded === true && typeof api.push === 'function') {
//       clearInterval(iv);
//       api.push(['getCoupons', function (err, response) {
//         if (err) return;
//         var list = response && response.data && response.data.coupons;
//         var count = Array.isArray(list) ? list.length : 0;
        
//         document.querySelectorAll('[data-coupons-count]').forEach(function (el) {
//           el.textContent = count + (count === 1 ? ' Reward' : ' Rewards');
//           if (el.parentElement) el.parentElement.hidden = false;
//         });
//       }]);
//     } else if (++tries >= 50) {
//       clearInterval(iv);
//     }
//   }, 200);
// })();

